import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Fab,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import moment from "moment-timezone";
import { useNavigate, useParams } from "react-router-dom";
import { useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "../../../context/AuthContext";
import { LoadingButton } from "@mui/lab";
import ReactSignatureCanvas from "react-signature-canvas";
import ShopS3PresignedCardImage from "../../../components/ShopS3PresignedCardImage";
import { CameraAlt, Close, Upload } from "@mui/icons-material";
import FileAttachment from "../../../components/FileUpload/FileAttachment";
import ShopS3PresignedImage from "../../../components/ShopS3PresignedImage";
import DownloadIcon from "@mui/icons-material/Download";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import getPickupPackingListDocDefinition from "../../../pdf-helpers/PickupPackingList";
import QRCode from "qrcode";
import OrderTitle from "../../../components/OrderTitle";
import { SnackAlertContext } from "../../../context/SnackAlertContext";
import PdfViewer from "../../../components/PdfViewer";
import PackingListDialog from "./PackingListDialog";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const PickupReviewAndSign = () => {
  const { orderId, pickupId } = useParams();
  const { ezorder, authToken } = useContext(AuthContext);
  const { openSnackMessage } = useContext(SnackAlertContext);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const [orderDetails, setOrderDetails] = useState(null);
  const [pickupDetails, setPickupDetails] = useState(null);
  const [pickupTransactions, setPickupTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const signatureRef = useRef();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [receivedBy, setReceivedBy] = useState("");
  const [disclaimerChecked, setDisclaimerChecked] = useState(false);
  const [imageUploads, setImageUploads] = useState([]);
  const uploadRef = useRef();
  const cameraRef = useRef();
  const [uploadedFileS3Keys, setUploadedFileS3Keys] = useState([]);
  const [pickupImages, setPickupImages] = useState([]);
  const [pdfLocalDataUrl, setPdfLocalDataUrl] = useState(null);
  const [packingListDialogOpen, setPackingListDialogOpen] = useState(false);

  const getPickupDetails = async (orderId, pickupId) => {
    try {
      setLoading(true);
      const res = await ezorder.get(
        `/shop/jobs/${orderId}/pickup-history/pickups/${pickupId}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      );
      setPickupTransactions(res.data.pickup.transactions);
      setPickupDetails(res.data.pickup);
      let imgs = res.data.pickup.images;
      setPickupImages(imgs);
    } catch (error) {
      console.log(error);
      // setError(error);
    } finally {
      setLoading(false);
    }
  };

  const getOrderWithPickupHistory = async (orderId) => {
    try {
      setLoading(true);
      const res = await ezorder.get(`/shop/jobs/${orderId}/pickup-history`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      setOrderDetails(res.data.order);
    } catch (error) {
      console.log(error);
      // setError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    let base64Signature = signatureRef.current
      .getTrimmedCanvas()
      .toDataURL("image/png");
    console.log("Signature base64Image", base64Signature);

    //setSignature(signature);
    const s3SignaturePath = await uploadSignature(base64Signature);
    console.log("Saved Signature S3 Path", s3SignaturePath);
    await signAndApprove(s3SignaturePath);
  };

  const createWritePresignedUrl = async (
    actionType,
    fileName,
    contentType,
    resource,
  ) => {
    const identifier =
      orderDetails.company && orderDetails.company.id
        ? orderDetails.company.id
        : "GUEST";

    const response = await ezorder.post(
      "/files/presignedUrl",
      { identifier, actionType, fileName, contentType, resource },
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      },
    );

    return response.data;
  };

  function base64toBlob(dataURI) {
    var byteString = atob(dataURI.split(",")[1]);
    var ab = new ArrayBuffer(byteString.length);
    var ia = new Uint8Array(ab);

    for (var i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: "image/jpeg" });
  }

  // Returns S3 Key generated during presigning
  const uploadSignature = async (base64Signature) => {
    try {
      setIsRefreshing(true);
      const presignedUrlResponse = await createWritePresignedUrl(
        "WRITE",
        "pickup-signature.png", // this name doesn't matter
        "image/png",
        "pickup-signatures",
      );
      const presignedUploadUrl = presignedUrlResponse.uploadUrl;
      const s3Key = presignedUrlResponse.key;

      //const fileBlob = await getBlob(signatureFile.uri);
      let fileBlob = base64toBlob(base64Signature);
      await fetch(presignedUploadUrl, {
        method: "PUT",
        body: fileBlob,
        headers: {
          "Content-Type": "image/png", // DO NOT PASS IN A BEARER TOKEN
        },
      });

      // TODO If you get a weird error or non-error, AMAZON might be returning XML which fetch doesn't support...
      // SO IT WILL NOT THROW AN ERROR EVEN WITH STATUS 400
      // console.log("fetchResult " + (await fetchResult.text()).toString());
      // console.log("fetchResult " + fetchResult.toString());
      console.log("S3 Key of Uploaded Signature", s3Key);
      return s3Key;
    } catch (error) {
      console.log(error);
      return null;
    } finally {
      setIsRefreshing(false);
    }
  };

  const signAndApprove = async (signatureS3Path) => {
    try {
      setIsRefreshing(true);
      const response = await ezorder.put(
        `/shop/jobs/${orderId}/pickup-history/pickups/${pickupId}/sign`,
        {
          signature: signatureS3Path,
          receivedBy,
          disclaimerChecked,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      );
      if (response.status == 200 || response.status == 201) {
        getOrderWithPickupHistory(orderId);
        getPickupDetails(orderId, pickupId);
        openSnackMessage(
          "success",
          pickupDetails && pickupDetails.pickupType == "DELIVERY"
            ? "Delivery Completed"
            : "Pickup Completed",
        );
      } else {
        openSnackMessage("error", response.response.data.error);
      }
    } catch (error) {
      console.log(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
        openSnackMessage("error", error.response.data.error);
      } else {
        // Something went really bad
        console.log(error);
        openSnackMessage("error", error);
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  const getBase64FromS3Key = async (s3Key) => {
    try {
      let keySplit = s3Key.split("/");
      let identifier = keySplit[0];
      let resource = keySplit[1];
      let fileName = keySplit[2];

      let requestUrl = "/files";
      let requestData = {
        fileName,
        resource,
        identifier,
      };

      let data = {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        params: requestData,
      };

      const response = await ezorder.get(requestUrl, data);
      const contentType = response.headers["content-type"];
      console.log("Base64 Response", response);
      // let base64 =
      //   `data:${contentType};base64,` +
      //   Buffer.from(response.data, "base64").toString("base64");
      let base64 = `data:${contentType};base64,${response.data}`;
      return base64;
      // return response.data;
    } catch (error) {
      throw Error("Unable to load presigned URL", { cause: error });
    }
  };

  const getPackingList = async () => {
    try {
      // TODO: QR CODE Image
      let qrDataURL = await QRCode.toDataURL(
        `${window.location.host}/job-pickups/${orderDetails.id}/pickups/${pickupId}`,
      )
        .then((url) => {
          return url;
        })
        .catch((err) => {
          console.error(err);
        });

      let signatureBase64 = null;
      if (pickupDetails.signature) {
        signatureBase64 = await getBase64FromS3Key(pickupDetails.signature);
      }

      console.log("QR CODE URL", qrDataURL);

      const docDef = getPickupPackingListDocDefinition(
        orderDetails,
        pickupDetails,
        qrDataURL,
        signatureBase64,
      );
      console.log(docDef);

      // pdfMake.createPdf(docDef).open();
      // pdfMake.createPdf(docDef).download(
      //   `PACKING-LIST.pdf`
      // );
      pdfMake.createPdf(docDef).getDataUrl((dataUrl) => {
        // const targetElement = document.querySelector("#iframeContainer");
        // const iframe = document.createElement("iframe");
        // iframe.src = dataUrl;
        // targetElement.appendChild(iframe);
        setPdfLocalDataUrl(dataUrl);
      });
    } catch (error) {
      console.log("ERROR", error);
      throw Error("Unable to load PDF", { cause: error });
    }
  };

  useEffect(() => {
    console.log("uploadedFileS3Keys", uploadedFileS3Keys);
  }, [uploadedFileS3Keys]);

  useEffect(() => {
    if ((orderId, pickupId)) {
      getOrderWithPickupHistory(orderId);
      getPickupDetails(orderId, pickupId);
    }
  }, [orderId, pickupId]);

  useEffect(() => {
    if (orderDetails && pickupDetails) getPackingList();
  }, [orderDetails, pickupDetails]);

  if (orderDetails)
    return (
      <Box>
        {/* <Box>
          <Box sx={{ display: "flex", flexDirection: "row" }}>
            <Box sx={{ flex: 1 }}>
              <OrderTitle order={orderDetails} includeLink={true} />
            </Box>
          </Box>
        </Box> */}
        <Typography variant="h6">
          <Typography variant="h5" sx={{ textAlign: "center" }}>
            {pickupDetails && pickupDetails.pickupType == "DELIVERY"
              ? "Delivery"
              : "Pickup"}{" "}
            #{pickupDetails ? pickupDetails.id : ""}
          </Typography>
        </Typography>
        {loading || !orderDetails ? (
          <Box
            sx={{
              width: "100%",
              minHeight: "200px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size="3rem" />
          </Box>
        ) : (
          <Box>
            {!pickupDetails.signature && !pickupDetails.signedOn ? (
              <Box
                sx={{
                  padding: "16px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="h6">Review & Sign Packing List</Typography>
                {/* <IconButton
                  sx={{ marginTop: "8px" }}
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    getPackingList();
                  }}
                >
                  Packing List
                  <DownloadIcon />
                </IconButton> */}
                {pdfLocalDataUrl && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Button
                      sx={{
                        width: "200px",
                        marginTop: "8px",
                        marginBottom: "16px",
                      }}
                      variant="outlined"
                      color="primary"
                      onClick={() => {
                        setPackingListDialogOpen(true);
                      }}
                    >
                      View Packing List
                    </Button>
                  </Box>
                )}

                <Typography sx={{ width: "100%" }}>Disclaimer</Typography>
                <Typography sx={{ width: "100%" }}>
                  We hereby accept the quantities listed herein, and release
                  Industry Metal Co of any discrepancies in regards to
                  quantities
                </Typography>

                <Box sx={{ marginTop: "16px" }}>
                  <Typography>
                    <Checkbox
                      style={{
                        margin: 0,
                        padding: 0,
                      }}
                      checked={disclaimerChecked}
                      onChange={(e) => {
                        // setSaveToCatalog(e.target.checked);
                        setDisclaimerChecked(e.target.checked);
                      }}
                    />
                    I HEREBY ACCEPT THE QUANTITY AND CONDITION OF THE MATERIALS
                    PACKAGED HEREIN
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    // justifyContent: isMediumScreen ? "center" : "flex-end",
                    marginBottom: "16px",
                  }}
                >
                  <Box>
                    <TextField
                      fullWidth
                      sx={{ marginTop: "16px", marginBottom: "16px" }}
                      label="Print Name"
                      value={receivedBy}
                      onChange={(e) => {
                        setReceivedBy(e.target.value);
                      }}
                    />
                    {/* <Typography>Signature</Typography> */}
                    <Card
                      sx={{
                        background: "#d2d2d2",
                        width: 310,
                        height: 100,
                      }}
                    >
                      <ReactSignatureCanvas
                        ref={signatureRef}
                        backgroundColor="rgba(0, 0, 0, 0)"
                        penColor="black"
                        canvasProps={{
                          width: 310,
                          height: 100,
                          className: "sigCanvas",
                        }}
                      />
                    </Card>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                      }}
                    >
                      <LoadingButton
                        loading={isRefreshing}
                        onClick={() => {
                          signatureRef.current.clear();
                        }}
                      >
                        Clear
                      </LoadingButton>
                      <LoadingButton
                        loading={isRefreshing}
                        onClick={() => {
                          if (signatureRef.current.isEmpty()) {
                            openSnackMessage("error", "Signature required");
                          } else {
                            handleApprove();
                          }
                        }}
                      >
                        Approve
                      </LoadingButton>
                    </Box>
                  </Box>
                </Box>
                <PackingListDialog
                  open={packingListDialogOpen}
                  handleClose={() => {
                    setPackingListDialogOpen(false);
                  }}
                  dataUrl={pdfLocalDataUrl}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  padding: "16px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="h6">
                  Review & Sign Packing List (Signed On{" "}
                  {moment(pickupDetails.signedOn)
                    .tz("America/Phoenix")
                    .format("MM/DD/YY")}
                  )
                </Typography>
                {/* <IconButton
                  sx={{ marginTop: "8px" }}
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    getPackingList();
                  }}
                >
                  Packing List
                  <DownloadIcon />
                </IconButton> */}
                {pdfLocalDataUrl && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Button
                      sx={{
                        width: "200px",
                        marginTop: "8px",
                        marginBottom: "16px",
                      }}
                      variant="outlined"
                      color="primary"
                      onClick={() => {
                        setPackingListDialogOpen(true);
                      }}
                    >
                      View Packing List
                    </Button>
                  </Box>
                )}
                <Typography sx={{ width: "100%" }}>Disclaimer</Typography>
                <Typography sx={{ width: "100%" }}>
                  We hereby accept the quantities listed herein, and release
                  Industry Metal Co of any discrepancies in regards to
                  quantities
                </Typography>

                <Box sx={{ marginTop: "16px" }}>
                  <Typography>
                    <Checkbox
                      style={{
                        margin: 0,
                        padding: 0,
                      }}
                      disabled
                      checked={pickupDetails.disclaimerChecked}
                    />
                    I HEREBY ACCEPT THE QUANTITY AND CONDITION OF THE MATERIALS
                    PACKAGED HEREIN
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    // justifyContent: isMediumScreen ? "center" : "flex-end",
                    marginBottom: "16px",
                  }}
                >
                  <Box>
                    <TextField
                      disabled
                      fullWidth
                      sx={{ marginTop: "16px", marginBottom: "16px" }}
                      label="Print Name"
                      value={pickupDetails.receivedBy}
                      InputProps={{ readOnly: true }}
                    />
                    {/* <Typography>Signature</Typography> */}

                    <Card
                      sx={{
                        background: "#d2d2d2",
                        // width: 310,
                        // height: 100,
                      }}
                    >
                      <ShopS3PresignedImage
                        style={
                          {
                            // width: "310px",
                            // height: "100px",
                          }
                        }
                        s3Key={pickupDetails.signature}
                      />
                    </Card>
                  </Box>
                </Box>
                <PackingListDialog
                  open={packingListDialogOpen}
                  handleClose={() => {
                    setPackingListDialogOpen(false);
                  }}
                  dataUrl={pdfLocalDataUrl}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
};

export default PickupReviewAndSign;
